import { Award, Disc, Radio, Mic, Headphones } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const PORTRAIT_IMG = "https://customer-assets.emergentagent.com/job_44de765f-cfdb-4824-9bb7-64054e32d5f5/artifacts/4pj28pje_image.png";
const AWARD_IMG = "https://customer-assets.emergentagent.com/job_44de765f-cfdb-4824-9bb7-64054e32d5f5/artifacts/qu23qqp8_image.png";

const milestones = [
  { year: "1980s", text: "Began DJing and producing in Nashville's early hip-hop scene", icon: Disc },
  { year: "1990s", text: "Production work with Pistol (Ruthless Records), Kool Daddy Fresh & regional artists", icon: Headphones },
  { year: "SAE", text: "Graduated SAE Institute of Technology — Diploma in Audio Technology", icon: Mic },
  { year: "2025", text: "Honored with the SEA Impact Award for contributions to southern entertainment", icon: Award },
  { year: "Now", text: "Founded DJsandMCMedia — empowering creators to own their sound", icon: Radio },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO
        title="About the Owner — Gary 'DJ KNS' Hayden"
        description="Meet Gary 'DJ KNS' Hayden, Nashville-based DJ, producer, and founder of DJsandMCMedia. Decades of hip-hop culture, production, and a mission rooted in independence and ownership."
      />
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-16 relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-[#0a0a0a] to-[#0a0a0a]" />
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div>
              <span className="inline-block bg-yellow-500/15 text-yellow-400 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
                The Man Behind the Platform
              </span>
              <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-4" data-testid="about-heading">
                Gary{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  "DJ KNS"
                </span>{" "}
                Hayden
              </h1>
              <p className="text-white/50 text-base md:text-lg leading-relaxed mb-6">
                Nashville-based DJ, producer, and media entrepreneur whose influence spans decades of hip-hop culture and independent music development.
              </p>
              <blockquote className="border-l-4 border-purple-500 pl-5 py-2" data-testid="about-quote">
                <p className="text-white/80 text-lg italic font-medium">
                  "Streams don't build wealth… ownership does."
                </p>
              </blockquote>
            </div>

            {/* Portrait */}
            <div className="flex justify-center md:justify-end">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-purple-500/40 to-pink-500/40 rounded-2xl blur-lg" />
                <img
                  src={PORTRAIT_IMG}
                  alt="Gary DJ KNS Hayden"
                  className="relative w-72 h-72 sm:w-80 sm:h-80 object-cover rounded-2xl shadow-2xl"
                  data-testid="about-portrait"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bio */}
      <section className="py-16 px-6" data-testid="about-bio">
        <div className="max-w-3xl mx-auto space-y-6 text-white/60 text-base leading-relaxed">
          <p>
            With roots dating back to the early 1980s, Gary built his name through live DJ performances, custom mixtapes, and production work that helped shape the sound of Nashville's local scene.
          </p>
          <p>
            A graduate of <strong className="text-white/80">SAE Institute of Technology</strong> with a diploma in Audio Technology, Gary combined formal education with real-world experience to develop a deep understanding of sound, production, and broadcast media.
          </p>
          <p>
            His early production credits include work with artists such as <strong className="text-white/80">Pistol</strong>, whose record gained national attention through <strong className="text-white/80">Ruthless Records</strong>, as well as projects with <strong className="text-white/80">Kool Daddy Fresh</strong> and other regional talent.
          </p>
          <p>
            Throughout his career, Gary has shared stages with and contributed to an era of music that includes some of hip-hop's most influential names, earning recognition as one of Nashville's pioneers in the culture.
          </p>
        </div>
      </section>

      {/* Timeline / Milestones */}
      <section className="py-16 px-6 bg-white/[0.02]" data-testid="about-milestones">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-base md:text-lg font-bold text-center text-white mb-12">Career Milestones</h2>
          <div className="space-y-0">
            {milestones.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} className="flex gap-5 group" data-testid={`milestone-${i}`}>
                  {/* Line + Dot */}
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-purple-500/15 border border-purple-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/25 transition">
                      <Icon className="w-4 h-4 text-purple-400" />
                    </div>
                    {i < milestones.length - 1 && <div className="w-px flex-1 bg-white/10 my-1" />}
                  </div>
                  {/* Content */}
                  <div className="pb-8">
                    <span className="text-purple-400 text-xs font-bold tracking-wider uppercase">{m.year}</span>
                    <p className="text-white/70 text-sm mt-1">{m.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEA Award Section */}
      <section className="py-20 px-6" data-testid="about-award">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/5 border border-yellow-500/20 rounded-3xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-5 h-5 text-yellow-400" />
                  <span className="text-yellow-400 text-xs font-bold tracking-widest uppercase">2025 Honoree</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">SEA Impact Award</h2>
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  In 2025, Gary was honored with the <strong className="text-white/80">SEA Impact Award</strong> for his contributions to the growth of entertainment in the southern United States — further solidifying his legacy and ongoing influence in hip-hop culture and independent media.
                </p>
              </div>
              <div className="relative min-h-[280px] md:min-h-0">
                <img
                  src={AWARD_IMG}
                  alt="Gary DJ KNS Hayden receiving the 2025 SEA Impact Award"
                  className="w-full h-full object-cover"
                  data-testid="about-award-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / DJsandMCMedia */}
      <section className="py-16 px-6 bg-white/[0.02]" data-testid="about-mission">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-base md:text-lg font-bold text-white mb-6">The Vision</h2>
          <p className="text-white/60 text-base leading-relaxed mb-8">
            Today, Gary is the driving force behind <strong className="text-white/80">DJsandMCMedia</strong> — a platform built to give DJs, artists, and creators control over their content, audience, and revenue.
          </p>
          <p className="text-white/50 text-sm mb-8">His mission is simple:</p>
          <blockquote className="border-l-4 border-purple-500 pl-6 text-left max-w-md mx-auto mb-10">
            <p className="text-white/80 text-lg italic font-medium leading-relaxed">
              Ownership over streams.<br />
              Legacy over hype.<br />
              Platform over permission.
            </p>
          </blockquote>
          <p className="text-white/60 text-base leading-relaxed mb-10">
            Through radio, media, and technology, he continues to build an ecosystem where creators don't just participate in the culture — they own it.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-8 py-3 rounded-xl transition font-semibold text-sm"
            data-testid="about-cta"
          >
            <Radio className="w-4 h-4" /> Explore the Platform
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
